import { v4 as uuidv4 } from 'uuid'

import { REGEXP } from 'shared/consts/regExp'
import { uploadPhoto } from 'modulesCommon/graphql/mutations'
import { IEmbedeedData } from 'shared/types/Quill'
import { isEmbededErrorVar } from 'modulesCommon/store'

export class EmbedUrl {
  url: URL

  constructor(url: string) {
    this.url = new URL(url)
  }

  toCode = async () => {
    if (this.isTikTok()) {
      return await this.tikTokEmbed()
    }
    return this.isYouTube() ? this.youTubeEmbed() : { code: this.url.toString() }
  }

  private isYouTube = () => REGEXP.YOUTUBE_SHORT.test(this.url.hostname)
  private isTikTok = () => REGEXP.TIKTOK_SHORT.test(this.url.hostname)

  private extractVideoUrl = (url: string): string => {
    const match = url.match(REGEXP.YOUTUBE_FULL) || url.match(REGEXP.YOUTUBE_SHARE)

    return match ? `${match[1] || 'https'}://www.youtube.com/embed/${match[2]}?showinfo=0` : url
  }

  private tikTokEmbed = async (): Promise<string> => {
    const urlString = this.url.toString()

    const resData = await this.getTikTokData(urlString)

    if (resData?.html && resData?.thumbnail_url) {
      const file = await this.downloadImage(resData.thumbnail_url, resData.title)

      if (file) {
        const thumbnailImg = await uploadPhoto(file, false)

        const embededData = { ...resData, thumbnail_url: thumbnailImg.Location }

        return this.createEmbededResponseElement(embededData)
      }
    } else {
      this.setErrorMessage()
    }

    return urlString
  }

  private youTubeEmbed = (): string => {
    const urlString = this.url.toString()

    return `<iframe src='${this.extractVideoUrl(urlString)}' class='ql-video' frameborder='0' allowfullscreen>`
  }

  private getTikTokData = async (url: string): Promise<IEmbedeedData | undefined> => {
    try {
      const result = await fetch(`https://www.tiktok.com/oembed?url=${url}`)

      return await result.json()
    } catch (e) {
      this.setErrorMessage()
      return
    }
  }

  private createEmbededResponseElement = (embededData: Record<string, any>) => {
    const tempDiv = document.createElement('div')

    const div = document.createElement('div')
    div.id = uuidv4()
    div.setAttribute('data-embeded-response', JSON.stringify(embededData))
    div.innerHTML = embededData.html

    tempDiv.appendChild(div)

    return tempDiv.innerHTML
  }

  private setErrorMessage = (): void => {
    isEmbededErrorVar(true)

    setTimeout(() => {
      isEmbededErrorVar(false)
    }, 3000)
  }

  private downloadImage = async (thumbnailUrl: string, thumbnailTitle: string): Promise<File | undefined> => {
    try {
      const photo = await fetch(thumbnailUrl)
      const blob = await photo.blob()
      const fileName = thumbnailTitle.replace(/[^a-z0-9]|\s+|\r?\n|\r/gim, '')

      return new File([blob], fileName, { type: blob.type })
    } catch (e) {
      return
    }
  }
}
